import { UseFormReturn } from "react-hook-form";
import { Inputs } from "../useUpsertProductForm";
import InfoModal from "../../Infomodal";
import { Checkbox } from "../../Checkbox";
import { RadioGroup } from "../../RadioGroup";
import Input from "../../Input";
import { stringToCurrency } from "~/utils/stringConversion";
import { useUserQuery } from "~/api/user";
import { Text } from "~/components/Text";

type Props = {
  form: UseFormReturn<Inputs>;
};

export const DeliveryInputs = ({ form }: Props) => {
  const { data, isFetching } = useUserQuery();
  const deliveryType = data?.store.deliveryType ?? "selfManaged";

  if (isFetching || deliveryType === "none") return null;

  const {
    control,
    register,
    watch,
    formState: { errors },
  } = form;

  const canOnlyBePickedUp = watch("canOnlyBePickedUp");
  const coveredByStoreDeliveryPrice = watch("coveredByStoreDeliveryPrice");
  const followsShipmondoGuidelines = watch("followsShipmondoGuidelines");

  const CanOnlyBePickedUpCheckbox = () => (
    <Checkbox
      control={control}
      id="canOnlyBePickedUp"
      label="Dette produkt kan kun afhentes"
      className="mb-8"
    />
  );

  if (canOnlyBePickedUp) {
    return <CanOnlyBePickedUpCheckbox />;
  }

  if (deliveryType === "shipmondo") {
    return (
      <>
        <CanOnlyBePickedUpCheckbox />

        <div className="mb-12">
          <Input
            placeholder="Fx. 1000"
            register={register}
            id="weightInGrams"
            type="number"
            label="Produktets vægt (i gram):"
            error={errors.weightInGrams}
            registerOptions={{
              valueAsNumber: true,
              required: "Vægt er påkrævet.",
            }}
          />
          {!errors.weightInGrams && (
            <small className="text-secondary-800 text-xs">
              Vægt er påkrævet, hvis du vil sende varen med Shipmondo.
            </small>
          )}
        </div>

        <div className="mb-12">
          <InfoModal
            id="followsShipmondoGuidelinesLabel"
            label="Efterlever dette produkt krav til standardforsendelser?"
          >
            <Text className="text-3xl mb-12">
              Krav til standardforsendelser
            </Text>
            <div className="flex flex-col gap-4">
              <p>
                Længden af en given pakke må ikke overstige 115 cm, eller to af
                siderne overstige 60 cm.
              </p>
              <p>
                Min. dimensioner er 15 x 10 x 1 cm og pakken må ikke veje under
                150 g.
              </p>
              <p>
                Hvis kravene overstiges vil fragtfirmaet sende en
                efteropkrævning.
              </p>
            </div>
          </InfoModal>
          <RadioGroup
            id="followsShipmondoGuidelines"
            aria-describedby="followsShipmondoGuidelinesLabel"
            control={control}
            error={errors.followsShipmondoGuidelines}
          >
            <RadioGroup.Item value="yes">Ja</RadioGroup.Item>
            <RadioGroup.Item value="no">Nej</RadioGroup.Item>
          </RadioGroup>
          {followsShipmondoGuidelines === "no" && (
            <small className="block text-secondary-800 text-xs mt-2">
              Du kan risikere en efteropkrævning, hvis kravene ikke overholdes.
            </small>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <CanOnlyBePickedUpCheckbox />

      <div className="mb-8">
        <div className="flex items-center mb-2">
          <label id="coveredByStoreDeliveryPriceLabel">
            Er dette produkt dækket af butikkens standard fragtpris?
          </label>
          <InfoModal id="coveredByStoreDeliveryPriceDescription" label="">
            <p className="mb-8">
              Du skal vælge 'Nej', hvis produktet ikke kan leveres inden for
              butikkens normale leveringspris.
              <br />I dette tilfælde skal du udfylde en særlig leveringspris,
              som erstatter den normale leveringspris.
            </p>
          </InfoModal>
        </div>
        <RadioGroup
          id="coveredByStoreDeliveryPrice"
          aria-describedby="coveredByStoreDeliveryPriceLabel"
          control={control}
        >
          <RadioGroup.Item value="yes">Ja</RadioGroup.Item>
          <RadioGroup.Item value="no">Nej</RadioGroup.Item>
        </RadioGroup>
      </div>

      {coveredByStoreDeliveryPrice === "yes" ? null : (
        <Input
          id="deliveryPrice"
          label="Fragtpris for denne vare:"
          placeholder="00,00"
          register={register}
          className="mb-12"
          type="currency"
          error={errors.deliveryPrice}
          registerOptions={{
            valueAsNumber: true,
            required: "Fragtpris er påkrævet",
            max: {
              value: 999999,
              message: `Pris kan ikke være højere end ${stringToCurrency({
                amount: 999999,
              })}`,
            },
            min: {
              value: 1,
              message: `Pris kan ikke være højere end ${stringToCurrency({
                amount: 1,
              })}`,
            },
          }}
        />
      )}
    </>
  );
};
